// shadow-rendering-drag-fix Gap 1B — ActivityRescheduleDialog
//
// Sibling to RescheduleLeagueMatchDialog (PR #243). Opens from
// ActivityDetailModal ("Flytta aktivitet" button) or from schedule-grid
// drag-drop on an activity-linked booking (via handleBookingDrop's new
// activity branch in AdminScheduleGrid).
//
// Form fields:
//   - Court selector (defaults to current court or drag-target)
//   - Date picker (defaults to current date or drag-target)
//   - Time picker, 15-min snap
//   - Duration readonly (preserved from current activity — RPC enforces)
//   - Reason (optional textarea, currently informational only — no audit log)
//
// On Confirm:
//   1. supabase.rpc('admin_reschedule_activity', {...}) — atomic, updates
//      the open_match row + the linked blocking booking in one transaction.
//   2. No chat / lock-notification fan-out (activities don't have those
//      semantics; deferred to follow-up if/when needed).
//
// Why not reuse MoveBookingDialog: that dialog recomputes destination
// price from pricing_rules and surfaces a phantom +N kr banner for
// price_total=0 activity bookings (the Gap 1B symptom).

import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface ActivityRescheduleSource {
  /** activities.id */
  activityId: string;
  /** Linked blocking booking (activities created via PR #248 useCreateActivity
   *  will have this set; legacy/orphan activities have it null). */
  bookingId: string | null;
  /** Current court. */
  currentCourtId: string | null;
  /** Current date (YYYY-MM-DD). */
  currentDate: string | null;
  /** Current start time (HH:MM:SS or HH:MM). */
  currentTime: string | null;
  /** Duration in minutes — preserved across the reschedule (RPC enforces). */
  durationMinutes: number;
  /** Header label, e.g. activity_title. */
  label: string;
}

interface ActivityRescheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: ActivityRescheduleSource;
  /** Available courts in the same hall/org. */
  courts: Array<{ id: string; name: string }>;
  /** Pre-fill TILL fields when invoked via drag-drop on the schedule grid. */
  initialTarget?: { courtId?: string; date?: string; time?: string } | null;
  /** Called after a successful reschedule so the parent can refresh data. */
  onRescheduled: () => void;
  /** Admin uid for the RPC — typically auth.uid() at session level. */
  adminId?: string;
}

const TIME_STEP_MIN = 15;

function snapTimeTo(value: string, stepMin: number): string {
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const total = h * 60 + m;
  const snapped = Math.round(total / stepMin) * stepMin;
  const hh = Math.floor(snapped / 60) % 24;
  const mm = snapped % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export function ActivityRescheduleDialog({
  open,
  onOpenChange,
  source,
  courts,
  initialTarget,
  onRescheduled,
  adminId,
}: ActivityRescheduleDialogProps) {
  const defaultCourt = initialTarget?.courtId ?? source.currentCourtId ?? courts[0]?.id ?? "";
  const defaultDate = initialTarget?.date ?? source.currentDate ?? "";
  const defaultTime = (initialTarget?.time ?? source.currentTime ?? "").slice(0, 5);

  const [courtId, setCourtId] = useState<string>(defaultCourt);
  const [dateStr, setDateStr] = useState<string>(defaultDate);
  const [timeStr, setTimeStr] = useState<string>(defaultTime);
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Re-default when dialog re-opens for a different activity
  useEffect(() => {
    if (open) {
      setCourtId(defaultCourt);
      setDateStr(defaultDate);
      setTimeStr(defaultTime);
      setReason("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source.activityId]);

  const hasChanged = useMemo(() => {
    return courtId !== source.currentCourtId
      || dateStr !== source.currentDate
      || timeStr.slice(0, 5) !== (source.currentTime ?? "").slice(0, 5);
  }, [courtId, dateStr, timeStr, source]);

  const handleConfirm = async () => {
    if (!courtId || !dateStr || !timeStr) {
      toast.error("Bana, datum och tid krävs");
      return;
    }
    const snappedTime = snapTimeTo(timeStr, TIME_STEP_MIN);
    setSubmitting(true);
    try {
      const { error: rpcErr } = await supabase.rpc("admin_reschedule_activity" as any, {
        _activity_id: source.activityId,
        _new_date: dateStr,
        _new_time: snappedTime,
        _new_court_id: courtId,
        _admin_id: adminId ?? null,
        _reason: reason || null,
      });
      if (rpcErr) {
        const msg = rpcErr.message ?? String(rpcErr);
        // Surface court-overlap as user-friendly message
        toast.error(msg.includes("court_overlap") || msg.includes("bokad")
          ? "Banan är redan bokad denna tid"
          : msg);
        return;
      }
      toast.success("Aktivitet flyttad");
      onRescheduled();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Flytta aktivitet</DialogTitle>
          <DialogDescription>{source.label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* FROM */}
          <div className="bg-muted/40 rounded p-2 text-xs">
            <div className="text-muted-foreground mb-1">Från</div>
            <div className="font-medium">
              {source.currentDate
                ? format(new Date(`${source.currentDate}T00:00:00`), "EEEE d MMM yyyy", { locale: sv })
                : "—"}
              {" kl "}
              {source.currentTime?.slice(0, 5) ?? "—"}
            </div>
            <div className="text-muted-foreground">
              {courts.find((c) => c.id === source.currentCourtId)?.name ?? "—"}
            </div>
          </div>

          <div className="flex items-center justify-center text-muted-foreground">
            <ArrowRight className="w-4 h-4" />
          </div>

          {/* TO */}
          <div className="space-y-2">
            <Label htmlFor="ard-court">Bana</Label>
            <Select value={courtId} onValueChange={setCourtId}>
              <SelectTrigger id="ard-court"><SelectValue placeholder="Välj bana" /></SelectTrigger>
              <SelectContent>
                {courts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="ard-date">Datum</Label>
                <Input id="ard-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="ard-time">Tid (15-min snap)</Label>
                <Input
                  id="ard-time"
                  type="time"
                  step={TIME_STEP_MIN * 60}
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  onBlur={(e) => setTimeStr(snapTimeTo(e.target.value, TIME_STEP_MIN))}
                />
              </div>
            </div>

            <div>
              <Label>Längd</Label>
              <Input value={`${source.durationMinutes} min`} readOnly className="bg-muted/30" />
            </div>

            <div>
              <Label htmlFor="ard-reason">Anledning (valfri)</Label>
              <Textarea
                id="ard-reason"
                placeholder="t.ex. spelarbyte, banservice…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={submitting || !hasChanged}>
            {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Bekräfta flytt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
